import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import LuxImage from "@/components/LuxImage";
import {
  ChevronRight,
  ChevronLeft,
  Plus,
  Zap,
  Sparkles,
} from "lucide-react";
import { useShop } from "@/context/ShopContext";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/lib/api";

/* --------------------------------------------------------------
   Slide definitions
   Each slide picks its "look" from the live catalogue by category
   + optional product-name preferences (soft matching).
--------------------------------------------------------------- */
const SLIDES = [
  {
    key: "spring",
    tag: "Spring · Summer Edit",
    title: "Wear",
    accent: "royalty",
    trail: ". Every day.",
    subtitle: "Timeless silhouettes crafted for the modern woman.",
    image:
      "https://images.unsplash.com/photo-1590588503756-08a4b2be5eb9?crop=entropy&cs=srgb&fm=jpg&q=95&w=1800",
    cta1: { to: "/collection/new", label: "Shop the Edit" },
    cta2: { to: "/collection/sale", label: "View Sale" },
    align: "left",
    look: {
      title: "Get the look",
      // pick 4 items — bias to shirts + one denim + one tee
      pick: (all) =>
        [
          all.find((p) => /ethereal/i.test(p.name)),
          all.find((p) => /ivory silk/i.test(p.name)),
          all.find((p) => /midnight/i.test(p.name)),
          all.find((p) => /signature black tee/i.test(p.name)),
        ].filter(Boolean),
    },
  },
  {
    key: "denim",
    tag: "The Denim Story",
    title: "Denim,",
    accent: "reimagined",
    trail: ".",
    subtitle: "Rigid indigo. Soft comfort. Made to move with you.",
    image:
      "https://images.unsplash.com/photo-1541099649105-f69ad21f3246?crop=entropy&cs=srgb&fm=jpg&q=95&w=1800",
    cta1: { to: "/category/jeans", label: "Shop Denim" },
    cta2: { to: "/collection/new", label: "New in Denim" },
    align: "left",
    look: {
      title: "Shop the story",
      pick: (all) => all.filter((p) => p.category === "jeans").slice(0, 4),
    },
  },
  {
    key: "silk",
    tag: "The Silk Story",
    title: "Softness,",
    accent: "distilled",
    trail: ".",
    subtitle: "Silk-touch shirts that move from desk to dinner.",
    image:
      "https://images.unsplash.com/photo-1656308859253-634095bc987b?crop=entropy&cs=srgb&fm=jpg&q=95&w=1800",
    cta1: { to: "/category/shirts", label: "Explore Shirts" },
    cta2: { to: "/collection/sale", label: "See Offers" },
    align: "right",
    look: {
      title: "Complete the look",
      pick: (all) => all.filter((p) => p.category === "shirts").slice(0, 4),
    },
  },
];

/* --------------------------------------------------------------
   Look Card — one-tap Add to Cart + Buy Now, luxury black/white/gold
--------------------------------------------------------------- */
const LookCard = ({ product, testIdPrefix }) => {
  const { addToCart } = useShop();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null); // 'add' | 'buy' | null

  const discount = product.discount_percent || 0;
  const finalPrice = Math.round((product.price * (100 - discount)) / 100);

  const doAdd = async (mode) => {
    setBusy(mode);
    const res = await addToCart({ product_id: product.id, size: "M", color: "", quantity: 1 });
    setBusy(null);
    if (res?.needsAuth) {
      navigate("/login", { state: { from: "/" } });
      return;
    }
    if (mode === "buy" && res?.success) {
      if (!user) {
        navigate("/login", { state: { from: "/checkout" } });
      } else {
        navigate("/checkout");
      }
    }
  };

  return (
    <div
      data-testid={`${testIdPrefix}-${product.id}`}
      className="group relative flex w-[190px] shrink-0 snap-start flex-col overflow-hidden border border-white/10 bg-black/60 backdrop-blur-md transition-all duration-300 hover:-translate-y-1 hover:border-[#D4AF37]/60"
    >
      <Link to={`/product/${product.id}`} className="relative block">
        <div className="relative aspect-[4/5] w-full overflow-hidden bg-black/40">
          <LuxImage
            src={product.image}
            alt={product.name}
            sizes="(min-width: 1024px) 190px, 190px"
            imgClassName="transition-transform duration-[900ms] group-hover:scale-[1.06]"
            showSkeleton={false}
          />
        </div>
        {discount > 0 && (
          <span className="absolute left-2 top-2 bg-[#D4AF37] px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white">
            -{discount}%
          </span>
        )}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/70 to-transparent" />
      </Link>
      <div className="flex flex-1 flex-col gap-1.5 p-3">
        <span className="text-[9px] uppercase tracking-[0.25em] text-[#D4AF37]">
          {product.category === "t-shirts" ? "T-Shirt" : product.category === "shirts" ? "Shirt" : product.category === "jeans" ? "Jeans" : (product.category || "Item").replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase())}
        </span>
        <div className="line-clamp-1 text-[13px] font-medium text-white">{product.name}</div>
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-semibold text-white">{formatINR(finalPrice)}</span>
          {discount > 0 && (
            <span className="text-[11px] text-white/50 line-through">{formatINR(product.price)}</span>
          )}
        </div>
        <div className="mt-2 grid grid-cols-2 gap-1.5">
          <button
            data-testid={`${testIdPrefix}-add-${product.id}`}
            onClick={() => doAdd("add")}
            disabled={!!busy}
            className="flex items-center justify-center gap-1 border border-white/40 py-2 text-[10px] font-semibold uppercase tracking-widest text-white transition-colors hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-black disabled:opacity-60"
          >
            <Plus className="h-3 w-3" /> {busy === "add" ? "…" : "Add"}
          </button>
          <button
            data-testid={`${testIdPrefix}-buy-${product.id}`}
            onClick={() => doAdd("buy")}
            disabled={!!busy}
            className="flex items-center justify-center gap-1 bg-[#D4AF37] py-2 text-[10px] font-semibold uppercase tracking-widest text-black transition-colors hover:bg-white disabled:opacity-60"
          >
            <Zap className="h-3 w-3" /> {busy === "buy" ? "…" : "Buy"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* --------------------------------------------------------------
   Shop-this-look strip that swaps in sync with active slide
--------------------------------------------------------------- */
const ShopThisLook = ({ slide, products }) => {
  const picks = useMemo(() => {
    if (!products?.length) return [];
    const items = slide.look.pick(products);
    if (items.length >= 3) return items.slice(0, 4);
    // fallback: fill from all products for that hero's theme
    const filler = products
      .filter((p) => !items.find((x) => x.id === p.id))
      .slice(0, 4 - items.length);
    return [...items, ...filler].slice(0, 4);
  }, [slide, products]);

  if (picks.length === 0) return null;

  return (
    <div
      key={slide.key}
      data-testid={`shop-this-look-${slide.key}`}
      className="reveal mt-0 border-t border-white/10 bg-gradient-to-b from-black/95 to-black px-4 py-5 sm:px-6"
    >
      <div className="mb-3 flex items-end justify-between">
        <div>
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.35em] text-[#D4AF37]">
            <Sparkles className="h-3 w-3" /> {slide.look.title}
          </span>
          <div className="mt-1 font-heading text-lg text-white sm:text-xl">
            Shop <em className="not-italic text-gold-gradient">this look</em>
          </div>
        </div>
        <Link
          to={slide.cta1.to}
          className="hidden text-[11px] uppercase tracking-widest text-white/70 hover:text-[#D4AF37] sm:inline-flex sm:items-center sm:gap-1"
        >
          Shop all <ChevronRight className="h-3.5 w-3.5" />
        </Link>
      </div>
      <div
        className="scrollbar-hide -mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0"
        data-testid={`shop-this-look-scroll-${slide.key}`}
      >
        {picks.map((p, idx) => (
          <div key={p.id} className="reveal" style={{ animationDelay: `${idx * 70}ms` }}>
            <LookCard product={p} testIdPrefix={`look-${slide.key}`} />
          </div>
        ))}
      </div>
    </div>
  );
};

/* --------------------------------------------------------------
   Hero shell — smooth crossfade + Ken-Burns zoom + staggered text
--------------------------------------------------------------- */
const HomeHero = ({ products = [] }) => {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (paused) return;
    timer.current = setInterval(() => {
      setI((v) => (v + 1) % SLIDES.length);
    }, 6500);
    return () => clearInterval(timer.current);
  }, [paused]);

  const go = (d) => setI((v) => (v + d + SLIDES.length) % SLIDES.length);

  const active = SLIDES[i];

  return (
    <section
      className="mt-6 px-4 sm:px-6"
      data-testid="home-hero"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative overflow-hidden rounded-2xl shadow-2xl">
        {/* --------- Slide viewport --------- */}
        <div className="relative aspect-[4/5] w-full sm:aspect-[16/9] lg:aspect-[21/9]">
          {SLIDES.map((s, idx) => {
            const isActive = idx === i;
            return (
              <div
                key={s.key}
                className={`absolute inset-0 transition-opacity duration-[1100ms] ease-out ${
                  isActive ? "opacity-100" : "opacity-0"
                }`}
                aria-hidden={!isActive}
              >
                {/* Ken-Burns slow zoom on active slide */}
                <LuxImage
                  src={s.image}
                  alt={s.tag}
                  sizes="100vw"
                  priority={i === 0}
                  imgClassName={isActive ? "hero-zoom" : ""}
                  showSkeleton={false}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
                <div
                  className={`absolute inset-0 flex flex-col justify-end p-6 sm:p-10 lg:p-14 ${
                    s.align === "right" ? "items-end text-right" : "items-start text-left"
                  }`}
                >
                  <span
                    className={`text-[10px] uppercase tracking-[0.35em] text-white/90 ${
                      isActive ? "hero-fade-1" : "opacity-0"
                    }`}
                  >
                    {s.tag}
                  </span>
                  <h1
                    className={`mt-3 max-w-md font-heading text-4xl leading-[1.05] text-white sm:text-5xl md:text-6xl ${
                      s.align === "right" ? "ml-auto" : ""
                    } ${isActive ? "hero-fade-2" : "opacity-0"}`}
                  >
                    {s.title} <em className="not-italic text-gold-gradient">{s.accent}</em>
                    {s.trail}
                  </h1>
                  <p
                    className={`mt-3 max-w-sm text-sm text-white/85 sm:text-base ${
                      isActive ? "hero-fade-3" : "opacity-0"
                    }`}
                  >
                    {s.subtitle}
                  </p>
                  <div
                    className={`mt-6 flex flex-wrap gap-3 ${s.align === "right" ? "justify-end" : ""} ${
                      isActive ? "hero-fade-4" : "opacity-0"
                    }`}
                  >
                    <Link
                      to={s.cta1.to}
                      data-testid={`hero-cta-primary-${s.key}`}
                      className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-xs font-semibold uppercase tracking-widest text-black transition-transform hover:-translate-y-0.5"
                    >
                      {s.cta1.label} <ChevronRight className="h-4 w-4" />
                    </Link>
                    {s.cta2 && (
                      <Link
                        to={s.cta2.to}
                        className="inline-flex items-center gap-2 rounded-full border border-white/60 px-6 py-3 text-xs font-semibold uppercase tracking-widest text-white transition-colors hover:bg-white hover:text-black"
                      >
                        {s.cta2.label}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Prev / Next */}
          <button
            data-testid="hero-prev"
            onClick={() => go(-1)}
            className="absolute left-3 top-1/2 hidden -translate-y-1/2 items-center justify-center rounded-full bg-white/90 p-2.5 text-black shadow-md backdrop-blur transition-all hover:bg-[#D4AF37] hover:text-white sm:flex"
            aria-label="Previous slide"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            data-testid="hero-next"
            onClick={() => go(1)}
            className="absolute right-3 top-1/2 hidden -translate-y-1/2 items-center justify-center rounded-full bg-white/90 p-2.5 text-black shadow-md backdrop-blur transition-all hover:bg-[#D4AF37] hover:text-white sm:flex"
            aria-label="Next slide"
          >
            <ChevronRight className="h-4 w-4" />
          </button>

          {/* Dots */}
          <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2" data-testid="hero-dots">
            {SLIDES.map((_, idx) => (
              <button
                key={idx}
                data-testid={`hero-dot-${idx}`}
                onClick={() => setI(idx)}
                className={`h-[3px] transition-all duration-500 ${
                  idx === i ? "w-10 bg-[#D4AF37]" : "w-4 bg-white/60 hover:bg-white/90"
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>

        {/* --------- Shop This Look strip (integrated inside the hero card) --------- */}
        <ShopThisLook slide={active} products={products} />
      </div>
    </section>
  );
};

export default HomeHero;
